import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Sparkles, Layers } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Slot = ({ side, label, glow, value, onPick, onClear, cards }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const shown = cards.filter((c) => !q.trim() || c.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="border border-pb-border bg-pb-elevated/40 p-3" data-testid={`backdrop-slot-${side}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[9px] font-black uppercase tracking-[0.25em] ${glow}`}>{label}</span>
        <button data-testid={`backdrop-keep-original-${side}`} onClick={() => { onClear(); setOpen(false); }}
          className={`text-[8px] font-black uppercase tracking-widest px-2 py-1 border transition-colors ${!value ? "bg-zinc-200 text-black border-zinc-200" : "border-pb-border text-zinc-500 hover:text-white"}`}>
          Keep Original
        </button>
      </div>
      {value ? (
        <div className="flex items-center gap-2 mb-2">
          <img src={`${BACKEND}${value.url}`} alt="" className="w-10 h-14 object-contain bg-black border border-pb-border" />
          <span className="text-xs font-semibold truncate">{value.name}</span>
        </div>
      ) : (
        <div className="text-[10px] text-zinc-600 uppercase tracking-widest mb-2">Original artwork</div>
      )}
      <button data-testid={`backdrop-pick-${side}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-center gap-1.5 border border-pb-cyan/50 text-pb-cyan text-[9px] font-black uppercase tracking-widest py-1.5 hover:bg-pb-cyan hover:text-black transition-colors">
        <Search className="w-3 h-3" /> {open ? "Close picker" : "Pick a card from the pool"}
      </button>
      {open && (
        <div className="mt-2">
          <input data-testid={`backdrop-search-${side}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cards…"
            className="w-full bg-black/50 border border-pb-border px-2 py-1.5 text-xs mb-2 focus:outline-none focus:border-pb-cyan" />
          <div className="grid grid-cols-4 gap-1.5 max-h-44 overflow-y-auto">
            {shown.map((c) => (
              <button key={c.url} data-testid={`backdrop-card-${side}-${c.url.split("/").pop()}`}
                onClick={() => { onPick(c); setOpen(false); }} title={c.name}
                className="border border-pb-border hover:border-pb-cyan transition-colors bg-black p-0.5">
                <img src={`${BACKEND}${c.url}`} alt={c.name} loading="lazy" className="w-full aspect-[3/4] object-contain" />
                <div className="text-[7px] truncate text-zinc-400">{c.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ArenaBackdropPanel() {
  const [mode, setMode] = useState("overlay");
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);

  useEffect(() => {
    api.get("/cardwars/backdrop").then(({ data }) => {
      setMode(data.mode || "overlay");
      setLeft(data.left);
      setRight(data.right);
      setAiPreview(data.image_url);
    }).catch(() => {});
    api.get("/cardwars/admin/backdrop/cards").then(({ data }) => setCards(data)).catch(() => {});
  }, []);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cardwars/admin/backdrop", {
        mode, left_url: left?.url || null, left_name: left?.name || null,
        right_url: right?.url || null, right_name: right?.name || null,
      });
      setAiPreview(data.image_url);
      toast.success(mode === "ai" ? "Arena re-painted — live in Card Wars 🎨" : "Arena backdrop updated ⚡");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="arena-backdrop-panel">
      <p className="text-xs text-zinc-500 mb-3">Swap the giant glowing cards in the Card Wars arena for real cards from the pool — feature the hot cards of the season. Keep either side original with its toggle.</p>
      <div className="flex gap-2 mb-3">
        <button data-testid="backdrop-mode-overlay" onClick={() => setMode("overlay")}
          className={`flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest px-3 py-2 border transition-colors ${mode === "overlay" ? "bg-[#A3E635] text-black border-[#A3E635]" : "border-pb-border text-zinc-400 hover:text-white"}`}>
          <Layers className="w-3 h-3" /> Live Overlay — instant
        </button>
        <button data-testid="backdrop-mode-ai" onClick={() => setMode("ai")}
          className={`flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest px-3 py-2 border transition-colors ${mode === "ai" ? "bg-fuchsia-400 text-black border-fuchsia-400" : "border-pb-border text-zinc-400 hover:text-white"}`}>
          <Sparkles className="w-3 h-3" /> AI Re-paint — blended (~30s)
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <Slot side="left" label="Left card — green glow" glow="text-[#A3E635]" value={left} cards={cards}
          onPick={setLeft} onClear={() => setLeft(null)} />
        <Slot side="right" label="Right card — red glow" glow="text-pb-red" value={right} cards={cards}
          onPick={setRight} onClear={() => setRight(null)} />
      </div>
      <button data-testid="backdrop-apply-btn" onClick={apply} disabled={busy}
        className="w-full bg-yellow-400 text-black font-black uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {mode === "ai" ? "Re-painting the arena…" : "Applying…"}</> : "Apply to the Arena"}
      </button>
      {mode === "ai" && aiPreview && (
        <div className="mt-3" data-testid="backdrop-ai-preview">
          <div className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Current AI arena</div>
          <img src={`${BACKEND}${aiPreview}`} alt="AI arena" className="w-full border border-pb-border" />
        </div>
      )}
    </div>
  );
}
